import { Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { LocalizationService } from 'src/app/shared/services/localization.service';
import { DefectHelper } from 'src/app/view/defects/helpers/defect.helper';
import { IssueStatus } from 'src/lib/fliko/models/defects/issue.base';
import { EstateType } from 'src/lib/fliko/models/estate/estate.data';
import { GetPublicDefectStatusData, GetPublicDefectStatusResult } from 'src/lib/fliko/models/http/estateQrDefect.data';
import {
  PublicDefectStatusRouteData,
  parsePublicDefectStatusRouteData,
  toLocalizedSummaryDetail
} from '../../helpers/public-defect-status.helper';
import { RWDService } from '../../services/rwd.service';

@Component({
  selector: 'app-public-defect-status-page',
  templateUrl: './public-defect-status-page.component.html',
  styleUrls: ['./public-defect-status-page.component.scss']
})
export class PublicDefectStatusPageComponent implements OnInit, OnDestroy {
  public readonly IssueStatus = IssueStatus;

  public routeData: PublicDefectStatusRouteData | null = null;
  public statusData: GetPublicDefectStatusResult | null = null;
  public isLoading = true;
  public isInvalid = false;

  private subscription$ = new Subscription();

  constructor(
    public rwdService: RWDService,
    private route: ActivatedRoute,
    private functions: Functions,
    private ls: LocalizationService
  ) {}

  ngOnInit(): void {
    this.ls.applyPreferredPublicLang();
    this.subscription$.add(
      this.route.queryParams.subscribe((params) => {
        this.routeData = parsePublicDefectStatusRouteData(params);
        void this.loadStatus();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription$.unsubscribe();
  }

  public get statusKey(): string {
    return `defectType.${this.statusData?.status ?? IssueStatus.NONE}`;
  }

  public get statusClass(): string {
    return `public-defect-status__badge--${this.statusData?.status ?? IssueStatus.NONE}`;
  }

  public get requestLabel(): string {
    return this.statusData?.refId ?? this.statusData?.defectId ?? '-';
  }

  public get qrLocationTypeLabel(): string | null {
    const location = this.statusData?.location;
    if (!location?.locationType) return null;
    return DefectHelper.getLocationTypeHR(location.locationType, location.estateType ?? EstateType.COMMUNITY, this.ls);
  }

  public get propertyDetailsSummary(): string {
    const location = this.statusData?.location;

    return [
      toLocalizedSummaryDetail('navigation.estate', this.statusData?.estateName, this.ls),
      toLocalizedSummaryDetail('estate.qrPublicLocationType', this.qrLocationTypeLabel, this.ls),
      toLocalizedSummaryDetail('addr.blockNo', location?.buildingNumber, this.ls),
      toLocalizedSummaryDetail('addr.stairNo', location?.stairwayNumber, this.ls),
      toLocalizedSummaryDetail('addr.floor', location?.floor, this.ls),
      toLocalizedSummaryDetail('addr.number', location?.number, this.ls)
    ]
      .filter((detail): detail is string => !!detail)
      .join(' • ');
  }

  private async loadStatus(): Promise<void> {
    this.isLoading = true;
    this.isInvalid = false;
    this.statusData = null;

    if (!this.routeData?.defectId || !this.routeData?.code) {
      this.isLoading = false;
      this.isInvalid = true;
      return;
    }

    try {
      const getPublicDefectStatus = httpsCallable<GetPublicDefectStatusData, GetPublicDefectStatusResult>(
        this.functions,
        'getPublicDefectStatus'
      );
      const result = (await getPublicDefectStatus(this.routeData)).data;

      if (!result?.success) throw new Error('getPublicDefectStatus rejected');

      this.statusData = result;
    } catch (error) {
      console.error('Failed to load public defect status', error);
      this.isInvalid = true;
    } finally {
      this.isLoading = false;
    }
  }
}
